import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import { theme } from 'lib/theme';
import { without } from 'lodash';
import React from 'react';
import { ListRenderItem } from '@shopify/flash-list';
import { useDispatch } from 'react-redux';
import {
  eventSelectScreenCancel,
  eventSelectScreenClose,
  eventSelectScreenSelection,
  NAVIGATE_MODAL_BACK,
  SET_INCOMING_VALUE,
  eventSelectScreenAdded,
} from 'store/actions';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { getSearchTermsFromSearchValue } from 'lib/riskbase';
import { pluralise } from 'lib/pluralise';
import { Txt } from 'components/Txt';
import { View } from 'react-native';
import { PageHeader } from 'components/PageHeader';
import { PageToolbar } from 'components/PageToolbar';
import { List } from 'components/List';

type SelectOptionInternal = {
  groupName: string;
  internalValue: number | string | null | undefined;
  name: string;
  value?: string;
  labelAlignTop?: boolean;
  valueNumberOfLines?: number;
  key: string;
  isNewGroup: boolean;
  radioValue?: boolean;
  checkedValue?: boolean;
  isFirst: boolean;
  splitterAbove: boolean;
  icon?: ICONS;
  iconColor?: string;
  showQuickChoose?: boolean;
};

const RenderItem = React.memo(
  (
    option: SelectOptionInternal & {
      onPress: (value: number | string | null | undefined) => void;
      onPressAdd:
        | ((value: number | string | null | undefined) => void)
        | undefined;
      onPressChoose:
        | ((value: number | string | null | undefined) => void)
        | undefined;
    },
  ) => (
    <>
      {option.splitterAbove && (
        <SeparatorHorizontal
          style={{
            marginTop: theme.padding - theme.paddingHorizontalHalf,
            marginBottom: theme.padding,
          }}
        />
      )}
      <Card
        label={option.isNewGroup ? option.groupName : undefined}
        style={{
          marginBottom: theme.padding / 2,
          marginTop: !option.isFirst && option.isNewGroup ? theme.padding : 0,
        }}
      >
        <CardRow
          key={option.internalValue || -1}
          label={option.name}
          value={option.value}
          labelAlignTop={option.labelAlignTop}
          labelNumberOfLines={0}
          radioValue={option.radioValue}
          checkedValue={option.checkedValue}
          onPress={() => option.onPress(option.internalValue)}
          onAction={
            option.onPressChoose
              ? () => option.onPressChoose?.(option.internalValue)
              : option.onPressAdd
                ? () => option.onPressAdd?.(option.internalValue)
                : undefined
          }
          actionIcon={
            option.onPressChoose ? ICONS.ArrowRightLight : ICONS.PlusLight
          }
          icon={option.icon}
          iconColor={option.iconColor}
        />
      </Card>
    </>
  ),
);

export const SelectScreen: React.FC<ModalScreenProps<ModalScreens.SELECT>> = ({
  route,
}) => {
  const dispatch = useDispatch();

  const {
    fieldName,
    incomingValueKey,
    multiSelect,
    options,
    returnType = 'array',
    text,
    title,
    valueNumberOfLines,
    value,
    searchable,
    enableSelectAll,
    requireValue,
    optionNoun,
    eventKey,
    showQuickChoose,
    eventKeyAdded,
    closeOnDone = true,
    hideRadioButton,
    showDoneButtonForSingleSelect = true,
    submitOnSelection = true,
    saveButtonText,
    subtitle,
  } = route.params;

  const initValue = React.useRef(
    !value ? [] : Array.isArray(value) ? value : [value],
  );
  const [internalValue, setInternalValue] = React.useState<(string | number)[]>(
    initValue.current,
  );
  const [searchValue, setSearchValue] = React.useState('');

  const handleSearch = (newSearchValue: string) =>
    setSearchValue(newSearchValue);

  const searchValues = getSearchTermsFromSearchValue(searchValue);

  const { options: nonFlatOptions, filteredOutButSelected } = options.reduce<{
    isFirst: boolean;
    splitterBeforeNext: boolean;
    options: SelectOptionInternal[][];
    groupKeys: Record<string, number>;
    filteredOutButSelected: SelectOptionInternal[];
  }>(
    (acc, option) => {
      if (option === '-') {
        if (!acc.isFirst) {
          acc.splitterBeforeNext = true;
        }
        return acc;
      }
      const selected = option.internalValue
        ? internalValue.includes(option.internalValue)
        : !internalValue.length;
      const details = option.name.toLowerCase();
      if (
        !searchValues.length ||
        searchValues.every((searchVal) => details.includes(searchVal))
      ) {
        const groupName = option.groupName || '';
        let isNewGroup = false;
        if (acc.groupKeys[groupName] === undefined) {
          isNewGroup = true; // acc.options.length > 0;
          acc.groupKeys[groupName] = acc.options.length;
          acc.options[acc.groupKeys[groupName]!] = [];
        }
        acc.options[acc.groupKeys[groupName]!]!.push({
          name: option.name,
          value: option.value,
          labelAlignTop: option.labelAlignTop,
          internalValue: option.internalValue,
          key: `${option.internalValue}-${option.name}`,
          radioValue: !multiSelect && !hideRadioButton ? selected : undefined,
          checkedValue: multiSelect ? selected : undefined,
          groupName,
          isNewGroup,
          splitterAbove: acc.splitterBeforeNext,
          isFirst: acc.isFirst,
          icon: option.icon,
          iconColor: option.iconColor,
        });
        acc.isFirst = false;
        acc.splitterBeforeNext = false;
      } else if (selected && multiSelect) {
        acc.filteredOutButSelected.push({
          name: option.name,
          value: option.value,
          labelAlignTop: option.labelAlignTop,
          internalValue: option.internalValue,
          key: `${option.internalValue}-${option.name}`,
          radioValue: false,
          checkedValue: true,
          groupName: '',
          isNewGroup: false,
          isFirst: false,
          splitterAbove: false,
          icon: option.icon,
          iconColor: option.iconColor,
        });
        acc.splitterBeforeNext = false;
      }
      return acc;
    },
    {
      isFirst: true,
      splitterBeforeNext: false,
      options: [],
      groupKeys: {},
      filteredOutButSelected: [],
    },
  );

  const flatOptions = nonFlatOptions.flat();

  const handleSave = React.useCallback(
    (newValue: (string | number)[]) => {
      if (closeOnDone) {
        dispatch(NAVIGATE_MODAL_BACK([ModalScreens.SELECT]));
      }
      if (fieldName && incomingValueKey) {
        dispatch(
          SET_INCOMING_VALUE({
            key: incomingValueKey,
            value: {
              [fieldName]: newValue.length
                ? returnType === 'string'
                  ? newValue[0]
                  : returnType === 'number'
                    ? parseInt(`${newValue[0]}`, 10)
                    : newValue
                : returnType === 'string'
                  ? ''
                  : returnType === 'number'
                    ? 0
                    : [],
            },
          }),
        );
      }
      if (eventKey) {
        dispatch(
          eventSelectScreenSelection({
            key: eventKey,
            numberValues: newValue.filter(
              (v) => typeof v === 'number',
            ) as number[],
            stringValues: newValue.filter(
              (v) => typeof v === 'string',
            ) as string[],
          }),
        );
      }
      // }
    },
    [closeOnDone, dispatch, eventKey, fieldName, incomingValueKey, returnType],
  );

  const handleSelect = React.useCallback(
    (newValue: string | number | null | undefined) =>
      setInternalValue((curInternalValue) => {
        const newInternalValue = !newValue
          ? []
          : multiSelect
            ? curInternalValue.includes(newValue)
              ? without(curInternalValue, newValue)
              : [...curInternalValue, newValue]
            : [newValue];
        if (!multiSelect && submitOnSelection) {
          handleSave(newInternalValue);
        }
        return newInternalValue;
      }),
    [handleSave, multiSelect, submitOnSelection],
  );

  const handlePressChoose = React.useCallback(
    (newValue: string | number | null | undefined) => {
      if (eventKey && newValue) {
        if (closeOnDone) {
          dispatch(NAVIGATE_MODAL_BACK([ModalScreens.SELECT]));
        }
        dispatch(
          eventSelectScreenSelection({
            key: eventKey,
            numberValues: typeof newValue === 'number' ? [newValue] : [],
            stringValues: typeof newValue === 'string' ? [newValue] : [],
          }),
        );
      }
    },
    [closeOnDone, dispatch, eventKey],
  );

  const handlePressAdd = React.useCallback(
    (newValue: string | number | null | undefined) => {
      if (newValue && eventKeyAdded) {
        dispatch(
          eventSelectScreenAdded({
            key: eventKeyAdded,
            numberValue: typeof newValue === 'number' ? newValue : undefined,
            stringValue: typeof newValue === 'string' ? newValue : undefined,
          }),
        );
        dispatch(NAVIGATE_MODAL_BACK([ModalScreens.SELECT]));
      }
    },
    [dispatch, eventKeyAdded],
  );

  const handleClose = () => {
    dispatch(NAVIGATE_MODAL_BACK([ModalScreens.SELECT]));
    if (eventKey) {
      dispatch(
        eventSelectScreenCancel({
          key: eventKey,
        }),
      );
    }
  };

  useEsc(handleClose);

  const renderItem: ListRenderItem<SelectOptionInternal> = React.useCallback(
    ({ item }) => {
      return (
        <RenderItem
          name={item.name}
          value={item.value}
          valueNumberOfLines={valueNumberOfLines}
          labelAlignTop={item.labelAlignTop}
          key={item.key}
          internalValue={item.internalValue}
          isNewGroup={item.isNewGroup}
          groupName={item.groupName}
          radioValue={item.radioValue}
          checkedValue={item.checkedValue}
          onPress={handleSelect}
          onPressAdd={eventKeyAdded ? handlePressAdd : undefined}
          onPressChoose={
            eventKey && showQuickChoose ? handlePressChoose : undefined
          }
          isFirst={item.isFirst}
          splitterAbove={item.splitterAbove}
          icon={item.icon}
          iconColor={item.iconColor}
        />
      );
    },
    [
      handleSelect,
      eventKeyAdded,
      handlePressAdd,
      eventKey,
      showQuickChoose,
      handlePressChoose,
      valueNumberOfLines,
    ],
  );

  React.useEffect(() => {
    return () => {
      if (eventKey) {
        dispatch(
          eventSelectScreenClose({
            key: eventKey,
          }),
        );
      }
    };
  }, [dispatch, eventKey]);

  return (
    <Page
      header={
        <>
          <PageHeader
            onPressCancelButton={handleClose}
            onPressSaveButton={
              !showDoneButtonForSingleSelect && !multiSelect
                ? undefined
                : requireValue && !internalValue.length
                  ? null
                  : () => handleSave(internalValue)
            }
            saveButtonText={saveButtonText}
            title={
              (title || 'Select') + (searchValues.length ? ' (Filtered)' : '')
            }
            subtitle={
              multiSelect && internalValue.length
                ? pluralise(internalValue.length, '1 Selected', '{x} Selected')
                : subtitle
            }
          />
          {((multiSelect && enableSelectAll) || searchable) && (
            <PageToolbar
              onSearch={handleSearch}
              searchPlaceholder={`Search ${optionNoun || title}`}
              onSearchEsc={handleClose}
            ></PageToolbar>
          )}
        </>
      }
    >
      <List
        paddingTop={text ? 0 : theme.paddingPageVertical}
        ListHeaderComponent={
          text || !flatOptions.length ? (
            <>
              {!!text && (
                <View
                  style={{
                    backgroundColor: theme.colors.orange10,
                    paddingVertical: theme.padding,
                    paddingHorizontal: theme.padding * 2,
                    marginBottom: theme.padding,
                  }}
                >
                  <Txt size="small">{text}</Txt>
                </View>
              )}
              {!flatOptions.length && (
                <Card style={{ marginBottom: theme.padding / 2 }}>
                  <CardRow
                    placeholder={`No ${optionNoun || 'Options'} Found`}
                  />
                </Card>
              )}
            </>
          ) : null
        }
        ListFooterComponent={
          filteredOutButSelected.length ? (
            <>
              <Card
                label="Selected but hidden by filter"
                style={{
                  marginTop:
                    theme.paddingVertical - theme.paddingHorizontalHalf,
                }}
              ></Card>
              {filteredOutButSelected.map((option) => (
                <Card
                  key={option.key}
                  style={{
                    marginBottom: theme.padding / 2,
                  }}
                >
                  <CardRow
                    label={option.name}
                    checkedValue={option.checkedValue}
                    radioValue={option.radioValue}
                    onPress={() => handleSelect(option.internalValue)}
                  />
                </Card>
              ))}
            </>
          ) : null
        }
        keyExtractor={(item) => item.key}
        renderItem={renderItem}
        data={flatOptions}
      ></List>
    </Page>
  );
};
